// YTRepliesSetupModal — the full "How does this work?" instructional pop-up
// for the AI YouTube Comment Replies add-on. Static shell: every button is a
// no-op right now (visual bless-round before OAuth wiring). Sections 1–6 per
// FEATURE_YT_AI_REPLIES_SPEC.md §6. (Randy, Jul 2026)
import { useState, useEffect, useRef } from "react";
import { X, YoutubeLogo, Cube, Copy, CaretDown, CheckSquare, Square, ArrowRight, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ytApi } from "@/lib/ytApi";
import { YtreChannelPicker } from "@/components/YtreChannelPicker";
import { api } from "@/lib/api";
import { useSkuPrice } from "@/lib/useSkuPrice";
import { fmt$ } from "@/lib/usePublicPricing";

// Note: this constant is now overridden at runtime by the /callback-uri
// backend endpoint so future backend URL changes don't need a frontend
// redeploy. Kept as a fallback for offline/dev.
const REDIRECT_URI_FALLBACK = "https://belief-database-ai.emergent.host/api/youtube/callback";

const STEPS = [
  {
    n: 1, title: "Enable YouTube API access on Google Cloud",
    body: [
      "You need this once. If you've done it for another tool (TubeBuddy, VidIQ), you can skip.",
      "Click the link below to open Google Cloud Console:",
    ],
    link: { label: "console.cloud.google.com — YouTube API", url: "https://console.cloud.google.com/apis/library/youtube.googleapis.com" },
    tail: [
      "Sign in with the Google account that owns your YouTube channel.",
      "Click the big blue Enable button.",
      "Wait ~30 seconds until it flips to \"API Enabled\".",
    ],
  },
  {
    n: 2, title: "Create an OAuth Client ID",
    body: [
      "Same Google Cloud project, in the left nav go to:",
      "APIs & Services → Credentials → + CREATE CREDENTIALS → OAuth client ID",
      "Application type: Web application",
      "Name: My Silo YouTube Replies (anything, doesn't matter)",
      "Under Authorized redirect URIs, click + Add URI and paste this exact string:",
    ],
    copy: true, // rendered dynamically from the fetched REDIRECT_URI
    tail: [
      "Click Create. Google will show you a Client ID and a Client Secret.",
      "Copy both. You'll paste them in the next step. Don't share them — treat them like passwords.",
    ],
  },
  {
    n: 3, title: "Paste your Client ID & Client Secret into My Silo",
    body: ["Two labelled password-style input fields live inside this modal."],
    inputs: true,
  },
  {
    n: 4, title: "Sign in to YouTube (OAuth)",
    body: [
      "Tap the red button below. The Google consent screen opens in a new tab. Pick the account that owns the channel, agree to the read-comments + post-replies scopes.",
      "This modal auto-detects success — the button flips to green ✓ Connected as [Channel Name].",
      "If you own multiple channels, a small dropdown appears to pick which one this Silo pane manages (Channel 1 = Workbench, Channel 2 = Refinery).",
    ],
    connectBtn: true,
  },
  {
    n: 5, title: "You're connected — now the console runs the show",
    body: [
      "After Step 4 succeeds, close this pop-up. Everything else happens in the Console right below the locked card (on Workbench for Channel 1, Refinery for Channel 2).",
      "The Console has four tabs: Inbox · Settings · Analytics · Danger. Tap Settings to tune what you'd normally expect from a setup wizard — but it's all live, editable anytime, no wizard trap.",
    ],
    consoleTour: [
      { tab: "Inbox", copy: "Every AI-drafted reply waits here. Approve, edit, or reject each one. Auto-refreshes every ~90 seconds via the ambient poller so you never have to smash Refresh." },
      { tab: "Settings", copy: "Three knobs: (1) which videos it monitors — all, hand-picked, or keyword-filtered; (2) your reply signature template with {silo_name} and {silo_url} variables; (3) safety mode — draft-only, auto-after-10-approvals, or full-auto." },
      { tab: "Analytics", copy: "Last-7-days replies, clicks back to your silo, new silo visits, and total LLM spend at a glance." },
      { tab: "Danger", copy: "Instant Pause kill-switch and Disconnect. Pause holds every future draft. Disconnect revokes Google access but keeps your history intact." },
    ],
  },
  {
    n: 6, title: "Reply Inbox also mirrors inside the Silo Chat tab",
    body: [
      "Randy's daily-use tip: instead of scrolling to the bottom of Workbench every time you want to approve a draft, open the Silo Chat tab and scroll to the YouTube Reply Inbox section at the bottom.",
      "It has the same approve/edit/reject buttons as the Console Inbox tab, plus an \"Approve all visible\" bulk button for high-volume days.",
      "Channel 1 / Channel 2 tabs at the top of that inbox let you switch queues without leaving Chat.",
    ],
  },
  {
    n: 7, title: "The ambient poller — no button-tapping",
    body: [
      "Every 90 seconds, My Silo checks your connected channel(s) for new question comments. Drafts appear automatically in both the Console Inbox and the Chat Inbox.",
      "YouTube's API doesn't push comment notifications, so polling is the fastest legitimate path. Randy's typical creator burns ~1% of the daily YouTube quota — plenty of headroom.",
      "You can still tap Refresh manually anytime — instant check.",
    ],
  },
  {
    n: 8, title: "Money trail — where every AI dollar goes",
    body: [
      "Every drafted reply hits your BYOK LLM key OR your voucher pool (whichever is set on this Silo). Nothing free.",
      "Cost surfaces four places: (1) the small $ pill on each draft in the Inbox; (2) the Analytics tab \"LLM spend\" cell; (3) your Creator Dashboard; (4) the Admin Usage Report, tagged internal_source: youtube_ai_replies so YouTube spend is filterable.",
      "Typical creator burns under $3/mo on Gemini Flash. Claude Sonnet is ~10x, so pick your model based on volume.",
    ],
  },
];

const TROUBLESHOOTING = [
  { q: 'Google says my redirect URI doesn\'t match', a: "Recopy it from Step 2 exactly — case matters." },
  { q: "I connected the wrong channel", a: "Click Disconnect in Settings, then run Step 4 again." },
  { q: "Drafts aren't appearing", a: "Check that the YouTube API is enabled (Step 1) and your BYOK LLM key has balance." },
  { q: "A reply posted that I didn't like", a: "Hit Undo in the Reply Inbox (72-hour window). YouTube's API allows it." },
  { q: "How do I pause everything?", a: "Giant red Pause all replies button in Settings. Instant kill switch." },
  { q: "Can I export my reply history?", a: "Yes — CSV export in Settings." },
];

export default function YTRepliesSetupModal({ open, onClose, channelSlot = "channel1", siloId = null, surfaceTitle = null }) {
  const [openTS, setOpenTS] = useState(null);
  // Oct 3 2026 — the cost breakdown said "$50 one-time". Live from the
  // Creator Pro catalog now (`yt_ai_replies.day1_cents`).
  const skuCents = useSkuPrice("yt_ai_replies");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // Sep 16 2026 — one-shot guard + a stable handle on the consent opener, so
  // the message listener can retry a spent link without being re-created on
  // every render.
  const retriedStaleRef = useRef(false);
  const openConsentRef = useRef(null);
  const [redirectUri, setRedirectUri] = useState(REDIRECT_URI_FALLBACK);
  const [connectedChannel, setConnectedChannel] = useState(null); // { name, id }
  // Feb 2026 (Randy pt-yt-setup-fix) — persistent visible confirmation for
  // Step 3 and gating for Step 4. `keysSaved` flips true whenever the
  // backend has stored client credentials for this silo+slot, either from
  // this session's Save & continue tap OR a previous session detected on
  // modal open. When false, the red Connect button in Step 4 explains
  // why it's inert instead of failing silently.
  const [keysSaved, setKeysSaved] = useState(false);
  const [savedClientIdPreview, setSavedClientIdPreview] = useState("");
  // Feb 2026 (Randy pt-yt-modal-race) — during the ~200ms after modal
  // opens we don't yet know whether keys are on file. Show a spinner
  // state instead of leaving the Connect button visually dead so users
  // don't tap prematurely and get confused.
  const [checkingIntegration, setCheckingIntegration] = useState(false);
  // Feb 24, 2026 (Randy — modal slim-down) — collapsible "First-time
  // setup guide" wrapper. Auto-open for fresh silos (no credentials on
  // file yet, no channel connected) so first-timers still see the full
  // walkthrough. Auto-collapsed for returning users (already has creds
  // or a channel) so they get a small focused modal with a quick red
  // Connect button at the top, no scrolling through a book to reach it.
  // Populated once we know keysSaved / connectedChannel state.
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideResolved, setGuideResolved] = useState(false);
  // Feb 24, 2026 — quick-action disconnect state (top strip).
  const [disconnecting, setDisconnecting] = useState(false);

  // Iter160e (Randy pt-ytre-p1) — RETIRED: the old channel2-only "Mimic
  // Channel 1" toggle. Superseded by the CrossSiloDetectBanner at the
  // top of this modal, which offers a one-tap mirror from ANY of the
  // caller's existing OAuth rows across all silos + channel slots.

  // Fetch the authoritative callback URI from the backend on open so
  // Randy can copy it without worrying about hardcoded drift.
  useEffect(() => {
    if (!open) return;
    // Reset the guide state so a re-open resolves fresh from server truth.
    setGuideResolved(false);
    ytApi.callbackUri().then((uri) => uri && setRedirectUri(uri)).catch(() => {});
    // Also re-hydrate connection status + saved-keys status
    if (siloId) {
      setCheckingIntegration(true);
      ytApi.getIntegration(siloId, channelSlot).then((row) => {
        const _keysOnFile = !!row?.google_client_id;
        const _channelOnFile = !!row?.youtube_channel_name;
        if (_keysOnFile) {
          setKeysSaved(true);
          // Show a short preview so Randy can eyeball which client ID is stored
          const cid = row.google_client_id;
          setSavedClientIdPreview(cid.length > 24 ? `${cid.slice(0, 16)}…${cid.slice(-6)}` : cid);
        }
        if (_channelOnFile) {
          setConnectedChannel({ name: row.youtube_channel_name, id: row.youtube_channel_id });
        }
        // Feb 24, 2026 (Randy — modal slim-down) — auto-open the
        // "First-time setup guide" chevron ONLY when the user has
        // never connected anything on this silo/slot yet. Returning
        // users get a focused, small modal with quick-actions on top.
        setGuideOpen(!_keysOnFile && !_channelOnFile);
        setGuideResolved(true);
      }).catch(() => {
        // On error, default to open (safer to show more than hide the
        // instructions the user might need).
        setGuideOpen(true);
        setGuideResolved(true);
      }).finally(() => setCheckingIntegration(false));
    } else {
      // No silo context at all → treat as fresh, show the guide.
      setGuideOpen(true);
      setGuideResolved(true);
    }
  }, [open, siloId, channelSlot]);

  // Listen for the OAuth callback popup posting back a message.
  useEffect(() => {
    if (!open) return;
    const onMsg = (ev) => {
      if (!ev?.data || ev.data.type !== "yt-oauth-complete") return;
      setConnecting(false);
      if (ev.data.ok) {
        toast.success(ev.data.msg || "YouTube connected.");
        if (siloId) {
          ytApi.getIntegration(siloId, channelSlot).then((row) => {
            if (row?.youtube_channel_name) setConnectedChannel({ name: row.youtube_channel_name, id: row.youtube_channel_id });
          }).catch(() => {});
        }
        // Feb 2026 (Randy pt-yt-live-refresh) — broadcast so the Console
        // Card + Chat Pane refetch immediately without a page reload.
        try {
          window.dispatchEvent(new CustomEvent("yt-integration-updated", {
            detail: { siloId, slot: channelSlot },
          }));
        } catch (_e) { /* fine */ }
      } else if (ev.data.reason === "stale_link" && !retriedStaleRef.current) {
        // Sep 16 2026 (Randy) — the link he arrived on was spent. Don't
        // dead-end him: mint a fresh one and reopen, once. Guarded by a ref
        // so a genuinely broken setup can't loop popups.
        retriedStaleRef.current = true;
        toast.info("That sign-in link was already used — opening a fresh one.");
        openConsentRef.current?.().catch(() => {
          toast.error("Couldn't reopen Google sign-in. Tap Connect again.");
        });
      } else {
        toast.error(ev.data.msg || "Sign-in didn't complete.");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [open, siloId, channelSlot]);

  // Reset the one-shot retry guard whenever the modal is reopened.
  useEffect(() => { if (open) retriedStaleRef.current = false; }, [open]);

  if (!open) return null;

  const copyRedirect = () => {
    try { navigator.clipboard.writeText(redirectUri); toast.success("Redirect URI copied."); }
    catch { toast.error("Couldn't copy — select and copy manually."); }
  };

  const saveKeys = async () => {
    if (!siloId) return toast.error("Silo not identified.");
    if (!clientId.trim() || !clientSecret.trim()) return toast.error("Paste both Client ID and Client Secret.");
    setSavingKeys(true);
    try {
      const r = await ytApi.setup(siloId, channelSlot, clientId.trim(), clientSecret.trim());
      // Feb 2026 (Randy pt-yt-setup-fix) — visible + persistent success state.
      setKeysSaved(true);
      const cid = clientId.trim();
      setSavedClientIdPreview(cid.length > 24 ? `${cid.slice(0, 16)}…${cid.slice(-6)}` : cid);
      toast.success("Credentials saved. Now tap the red Connect button below.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save keys.");
    } finally {
      setSavingKeys(false);
    }
  };

  // Sep 16 2026 (Randy pt-yt-stale-auth-url) — ALWAYS mint a fresh consent
  // URL immediately before opening the window.
  //
  // This used to read a `window.__ytAuthUrl` global and only regenerate when
  // it was empty. That global was written by saveKeys and by the first
  // regeneration, shared across every silo and both channel slots, and never
  // invalidated — while the server rotates `oauth_state` on /setup,
  // /setup/mirror-from-integration, /auth-url, Disconnect, AND on a
  // successful callback. So the moment he completed one consent, the cached
  // URL was dead, and every later tap replayed it:
  //
  //   "neither the east tactics or the cosmic Eq. will work. its broken."
  //
  // It could only recover on a full page reload. Mirror was guaranteed to
  // fail this way, because it rotates the state server-side and the frontend
  // discarded the fresh URL it returned. One short request per click buys
  // correctness by construction: the link is single-use, which is the entire
  // point of OAuth state.
  const openConsentWindow = async () => {
    // Sep 26, 2026 (Randy — dead on iOS Safari, fine on desktop). Open the
    // window synchronously inside the tap, THEN navigate it once the
    // single-use URL arrives. Mobile browsers refuse window.open after an
    // await because the user gesture has expired; desktop is lenient, which
    // is why this split only ever showed up on a phone.
    const w = window.open("", "yt-oauth", "width=520,height=680,left=100,top=100");
    if (!w) throw new Error("popup-blocked");
    try {
      w.document.write(
        "<!doctype html><title>Google sign-in</title>"
        + "<body style='font:14px -apple-system,sans-serif;padding:24px;color:#333'>"
        + "Opening Google sign-in…</body>",
      );
    } catch (_e) { /* already navigated — fine */ }
    try {
      const r = await ytApi.authUrl(siloId, channelSlot);
      const url = r.authorization_url;
      if (!url) throw new Error("No sign-in URL came back.");
      w.location.href = url;
    } catch (e) {
      try { w.close(); } catch (_e2) { /* ignore */ }
      throw e;
    }
    return w;
  };
  openConsentRef.current = openConsentWindow;

  const connectChannel = async () => {
    if (!keysSaved) {
      return toast.error("Save your Google credentials in Step 3 first.");
    }
    setConnecting(true);
    try {
      await openConsentWindow();
    } catch (e) {
      setConnecting(false);
      if (e.message === "popup-blocked") {
        return toast.error("Popup blocked — allow popups for this site and try again.");
      }
      toast.error(e.response?.data?.detail || "Couldn't build the Google sign-in URL. Try re-saving your keys in Step 3.");
    }
  };

  // Sep 16 2026 (Randy) — the backend RETURNS a fresh authorization_url from
  // the mirror call and this used to discard it. Combined with the old cached
  // global that made Mirror fail every single time: it rotated `oauth_state`
  // server-side, then handed him a Connect button that replayed the now-dead
  // cached link. With `openConsentWindow` always minting fresh, Mirror only
  // has to save the credentials — which is all it was ever meant to do.
  const mirrorCredentials = async (sourceRow) => {
    try {
      const r = await api.post("/youtube/setup/mirror-from-integration", {
        source_integration_id: sourceRow.id,
        target_silo_id: siloId,
        target_channel_slot: channelSlot,
      });
      setKeysSaved(true);
      if (r.data?.client_id_preview) setSavedClientIdPreview(r.data.client_id_preview);
      toast.success(`Mirrored ${sourceRow.youtube_channel_name || "credentials"}. Now tap Connect below to bind this silo's channel.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mirror failed.");
    }
  };

  // Feb 24, 2026 (Randy — modal slim-down / wrong-channel swap) —
  // one-tap disconnect from the top quick-actions strip. Used when
  // the user is on the wrong channel and wants to reconnect a
  // different one. Preserves saved credentials (Client ID + Secret
  // stay on file) — only the OAuth binding is cleared, so the red
  // Connect button lights right back up for a same-modal re-consent.
  const disconnectChannel = async () => {
    if (!siloId) return;
    setDisconnecting(true);
    try {
      await ytApi.disconnect(siloId, channelSlot);
      setConnectedChannel(null);
      toast.success("Disconnected. Tap Connect to bind a different channel.");
      // Broadcast so the surrounding UI (Console cards, YTRE workspace)
      // refetches immediately.
      try {
        window.dispatchEvent(new CustomEvent("yt-integration-updated", {
          detail: { siloId, slot: channelSlot },
        }));
      } catch (_e) { /* fine */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't disconnect.");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-3 sm:p-4" onClick={onClose} data-testid={`yt-replies-setup-${channelSlot}`}>
      <div className="absolute inset-0 bg-[#1C1B1A]/60 backdrop-blur-sm" />
      <div className="relative z-10 bg-[#FDFCF8] rounded-2xl w-full max-w-[640px] max-h-[92dvh] flex flex-col shadow-2xl border hairline" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 sm:px-6 pt-5 pb-4 border-b hairline shrink-0 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
              <YoutubeLogo size={22} weight="fill" className="text-[#B54848]" />
              <Cube size={20} weight="duotone" className="text-[#4A5D4E]" />
            </div>
            <div className="min-w-0">
              <h2 className="font-serif-display text-xl sm:text-2xl text-[#1C1B1A] leading-tight">{surfaceTitle || "AI YouTube Comment Replies — Setup"}</h2>
              <p className="text-[12.5px] text-[#6B6A68] mt-1 leading-relaxed">Up and running in about 8 minutes. Nothing goes live until you approve it.</p>
            </div>
          </div>
          <button onClick={onClose} data-testid={`yt-replies-setup-close-${channelSlot}`}
            className="text-[#6B6A68] hover:text-[#1C1B1A] p-1 shrink-0" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Iter160e (Randy) — Cross-silo/module auto-detect banner.
            When the user has ANY existing YouTube OAuth row on another
            silo or channel slot, offer to mirror the credentials in one
            tap so they don't have to paste them again. Fetched from
            /youtube/integrations/mine. */}
        <CrossSiloDetectBanner
          currentSiloId={siloId}
          currentChannelSlot={channelSlot}
          onMirror={mirrorCredentials}
        />

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 sm:px-6 py-5 space-y-5 text-[13.5px] leading-relaxed">

          {/* Feb 24, 2026 (Randy — modal slim-down) — QUICK ACTIONS
              strip. Sits at the top of the modal body, immediately
              below the "Save yourself a step" mirror banner. Gives
              returning users a one-tap Connect / Disconnect flow
              without scrolling through the full setup guide below.
              Three states based on what's already on file:
                • connected      → green ✓ pill + Disconnect + swap red button
                • keys saved      → red Connect button, wired to same handler
                • fresh (nothing) → subtle prompt pointing to Step 3 */}
          <section
            data-testid="yt-setup-quick-actions"
            className="bg-white border hairline rounded-lg p-3.5 space-y-2.5"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
              Quick actions
            </div>
            {connectedChannel ? (
              <div className="space-y-2">
                <div
                  data-testid="yt-setup-quick-connected"
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#EAF3EC] border border-[#B9D4BE] text-[#2F6B3A] text-[13px] font-medium"
                >
                  <YoutubeLogo size={16} weight="fill" />
                  ✓ Connected as {connectedChannel.name}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    data-testid="yt-setup-quick-disconnect"
                    onClick={disconnectChannel}
                    disabled={disconnecting}
                    className="text-[12px] px-3 py-1.5 rounded-md border border-[#D4D0CA] bg-white text-[#4A4A48] hover:bg-[#F0F1EE] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {disconnecting ? "Disconnecting…" : "Disconnect"}
                  </button>
                  <button
                    type="button"
                    data-testid="yt-setup-quick-swap"
                    onClick={async () => {
                      await disconnectChannel();
                      // Then immediately open the Google chooser again
                      // so the user picks a different account/channel.
                      await connectChannel();
                    }}
                    disabled={disconnecting || connecting}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#B54848] hover:bg-[#8f3838] disabled:bg-[#B54848]/40 text-white text-[12px] font-medium disabled:cursor-not-allowed"
                  >
                    <YoutubeLogo size={14} weight="fill" />
                    Connect a different channel
                  </button>
                </div>
                {/* Sep 16 2026 (Randy) — REACHABLE #3. This strip was the
                    third place he went hunting for a quick switch and found
                    only Disconnect. Any channel already on file binds here
                    instantly; the red button stays for a genuinely new one. */}
                <div className="flex items-center gap-2 flex-wrap">
                  <YtreChannelPicker
                    siloId={siloId}
                    channelSlot={channelSlot}
                    connectedChannelId={connectedChannel?.id}
                    onSwitched={(c) => {
                      setConnectedChannel({ name: c.youtube_channel_name, id: c.youtube_channel_id });
                      try {
                        window.dispatchEvent(new CustomEvent("yt-integration-updated", {
                          detail: { siloId, slot: channelSlot },
                        }));
                      } catch (_e) { /* fine */ }
                    }}
                    label="Switch to a channel already on file"
                    testid="yt-setup-quick-switch"
                  />
                </div>
                <p className="text-[11px] text-[#6B6A68] italic">
                  Wrong channel? If you&apos;ve signed into it here before, switch straight to it above — no Google window, and the channel you&apos;re leaving stays connected everywhere else. Only a channel we&apos;ve never seen needs the red button.
                </p>
              </div>
            ) : keysSaved ? (
              <div className="space-y-2">
                <button
                  type="button"
                  data-testid="yt-setup-quick-connect"
                  onClick={connectChannel}
                  disabled={connecting || checkingIntegration}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#B54848] hover:bg-[#8f3838] disabled:bg-[#B54848]/40 text-white text-[13px] font-medium disabled:cursor-not-allowed"
                >
                  <YoutubeLogo size={16} weight="fill" />
                  {connecting ? "Waiting for consent…" : "Connect YouTube Channel"}
                </button>
                <p className="text-[11px] text-[#6B6A68]">
                  Credentials already on file (Client ID <span className="font-mono">{savedClientIdPreview || "•••"}</span>) — one tap opens the Google chooser.
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-[#FDF4EE] border border-[#EBC9C9] text-[#8A4844] text-[12.5px]">
                <span className="shrink-0 mt-0.5">↓</span>
                <div>
                  Google credentials not saved yet.{" "}
                  <button
                    type="button"
                    data-testid="yt-setup-quick-open-guide"
                    onClick={() => setGuideOpen(true)}
                    className="underline underline-offset-2 hover:no-underline font-medium"
                  >
                    Expand the first-time setup guide
                  </button>{" "}
                  to paste your Client ID + Secret, then come back here.
                </div>
              </div>
            )}
          </section>

          {/* Feb 24, 2026 (Randy — modal slim-down) — first-time setup
              guide is now wrapped in a chevron. Auto-opens on fresh
              silos (no keys / no channel on file), auto-collapses for
              returning users so the modal shrinks from a scroll-book
              to a small focused pane. Every sub-section (§1..§5) that
              used to render inline lives INSIDE this chevron so
              nothing is lost — just tucked below the quick actions. */}
          <section
            data-testid="yt-setup-guide-wrapper"
            className="border hairline rounded-lg overflow-hidden bg-white"
          >
            <button
              type="button"
              onClick={() => setGuideOpen((v) => !v)}
              data-testid="yt-setup-guide-toggle"
              className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left text-[13px] text-[#1C1B1A] hover:bg-[#F7FAF7]"
            >
              <span className="font-medium">
                First-time setup guide
                <span className="ml-2 text-[11px] font-normal text-[#6B6A68] italic">
                  {guideOpen ? "click to collapse" : "click to expand"}
                </span>
              </span>
              <CaretDown
                size={14}
                weight="bold"
                className={`shrink-0 text-[#4A5D4E] transition-transform ${guideOpen ? "rotate-180" : ""}`}
              />
            </button>
            {guideOpen && guideResolved && (
              <div
                data-testid="yt-setup-guide-body"
                className="border-t hairline px-4 py-4 space-y-5"
              >

          {/* § 1  What this does */}
          <section className="space-y-2.5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">① What this does — 60-second overview</div>
            <p className="text-[#3A3A38]">When a fan drops a comment on any of your YouTube videos, this tool reads it, checks your Silo&apos;s brain for an answer, and drafts a reply signed from your channel. <strong>You approve or edit every draft</strong> before anything is posted. Once you&apos;ve approved enough drafts in a row, you can flip on Auto-Mode and My Silo will post them for you within seconds of the comment landing.</p>
            <p className="text-[#3A3A38]">Every reply gets a soft link back to your Silo — so every YouTube comment becomes a marketing moment. Fans who ask the same question ten times a week? Answered before you&apos;ve had coffee.</p>
            <div className="bg-[#EAF3EC]/60 border border-[#B9D4BE] rounded-md p-2.5 text-[12px] text-[#2F6B3A]">✓ No coding · No cost per reply beyond your own LLM key · Kill switch is one tap.</div>
          </section>

          {/* § 2  Before you begin */}
          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">② Before you begin — what you&apos;ll need</div>
            <ul className="space-y-1.5 pl-1 text-[#3A3A38]">
              <li className="flex items-start gap-2"><Square size={14} weight="regular" className="mt-0.5 text-[#6B6A68] shrink-0" /> A YouTube channel you own (Creator Studio access)</li>
              <li className="flex items-start gap-2"><Square size={14} weight="regular" className="mt-0.5 text-[#6B6A68] shrink-0" /> Google account signed in on this browser</li>
              <li className="flex items-start gap-2"><Square size={14} weight="regular" className="mt-0.5 text-[#6B6A68] shrink-0" /> Your Silo already has a <strong>BYOK LLM key</strong> entered in Settings</li>
              <li className="flex items-start gap-2"><Square size={14} weight="regular" className="mt-0.5 text-[#6B6A68] shrink-0" /> ~8 minutes</li>
            </ul>
            <p className="text-[11.5px] text-[#6B6A68] italic">If any of the above is missing, complete it first — this setup won&apos;t lose your progress if you close the window.</p>
          </section>

          {/* § 3  Step-by-step setup */}
          <section className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">③ Step-by-step setup</div>
            {STEPS.map((s) => (
              <div key={s.n} className="border hairline rounded-lg p-3.5 bg-white" data-testid={`yt-setup-step-${s.n}`}>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#1C1B1A] text-[#FDFCF8] text-xs font-medium flex items-center justify-center shrink-0">{s.n}</div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="text-[14px] font-semibold text-[#1C1B1A]">{s.title}</div>
                    {s.body.map((line, i) => (<p key={i} className="text-[#3A3A38]">{line}</p>))}
                    {s.link && (
                      <a href={s.link.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[13px] text-[#2C6E92] hover:text-[#1B4E6E] hover:underline font-medium break-all">
                        🔗 {s.link.label} ↗
                      </a>
                    )}
                    {s.copy && (
                      <div className="flex items-center gap-2 bg-[#F0F1EE] border hairline rounded-md p-2 font-mono text-[11.5px] text-[#1C1B1A] break-all">
                        <span className="flex-1 min-w-0">{redirectUri}</span>
                        <button onClick={copyRedirect} data-testid="yt-setup-copy-redirect"
                          className="shrink-0 flex items-center gap-1 px-2 py-1 rounded bg-[#1C1B1A] text-[#FDFCF8] text-[10px] uppercase tracking-[0.14em] hover:bg-[#3A4A3E]">
                          <Copy size={11} weight="bold" /> Copy
                        </button>
                      </div>
                    )}
                    {s.tail && s.tail.map((line, i) => (<p key={i} className="text-[#3A3A38]">{line}</p>))}
                    {s.consoleTour && (
                      <div className="grid gap-1.5 pt-1" data-testid={`yt-setup-console-tour-${s.n}`}>
                        {s.consoleTour.map((row) => (
                          <div key={row.tab} className="flex items-start gap-2.5 border hairline rounded-md bg-white p-2.5">
                            <span className="shrink-0 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#8A4844] bg-[#FDF4EE] border border-[#EBC9C9] rounded px-2 py-0.5 mt-0.5">{row.tab}</span>
                            <p className="text-[12.5px] text-[#3A3A38] leading-relaxed">{row.copy}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {s.inputs && (
                      <div className="space-y-2 pt-1">
                        {/* Iter160e (Randy pt-ytre-p1) — Mimic Channel 1
                            toggle was RETIRED here. Superseded by the
                            new CrossSiloDetectBanner at the top of this
                            modal, which auto-detects OAuth on ANY of
                            the caller's silos + channel slots and
                            offers a one-tap mirror. Broader coverage,
                            same UX, no per-channel branching. */}
                        {keysSaved && (
                          <div
                            className="flex items-start gap-2 px-3 py-2 rounded-md bg-[#EAF3EC] border border-[#B9D4BE] text-[#2F6B3A] text-[12.5px]"
                            data-testid="yt-setup-keys-saved-status"
                          >
                            <CheckSquare size={16} weight="fill" className="shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <div className="font-medium">Credentials saved & encrypted.</div>
                              <div className="text-[11px] text-[#4A5D4E] mt-0.5">
                                Client ID on file: <span className="font-mono">{savedClientIdPreview || "•••"}</span>. Now go to Step 4.
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="block text-[11px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1">
                            Client ID {keysSaved && <span className="normal-case text-[10px] text-[#2F6B3A]">(re-paste only if changing)</span>}
                          </label>
                          {/* Feb 2026 — Chrome/Safari aggressively autofill anything that *looks* like
                              a login field, including OAuth key inputs. The combination below defeats it:
                              a decoy password field absorbs autofill, and each real input uses a unique
                              non-standard name + autoComplete="off" + a randomised id so the browser
                              can't heuristically match it to a saved credential. */}
                          <input type="text" name="prevent_autofill" autoComplete="off"
                            style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
                          <input type="password" name="prevent_autofill_pw" autoComplete="new-password"
                            style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
                          <input value={clientId} onChange={(e) => setClientId(e.target.value)}
                            data-testid="yt-setup-client-id"
                            name="yt-oauth-client-id-field"
                            id={`yt-oauth-client-id-${channelSlot}`}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            placeholder={keysSaved ? "Leave blank to keep saved value" : "Paste your Google OAuth Client ID"}
                            className="w-full bg-white border hairline rounded-md px-3 py-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                        </div>
                        <div>
                          <label className="block text-[11px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1">
                            Client Secret {keysSaved && <span className="normal-case text-[10px] text-[#2F6B3A]">(re-paste only if changing)</span>}
                          </label>
                          <div className="relative">
                            <input value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
                              type={showSecret ? "text" : "password"}
                              data-testid="yt-setup-client-secret"
                              name="yt-oauth-client-secret-field"
                              id={`yt-oauth-client-secret-${channelSlot}`}
                              autoComplete="new-password"
                              autoCorrect="off"
                              autoCapitalize="off"
                              spellCheck={false}
                              placeholder={keysSaved ? "Leave blank to keep saved value" : "Paste your Google OAuth Client Secret"}
                              className="w-full bg-white border hairline rounded-md px-3 py-2 pr-16 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                            <button type="button" onClick={() => setShowSecret((v) => !v)}
                              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] hover:text-[#1C1B1A] px-2 py-1">
                              {showSecret ? "Hide" : "Show"}
                            </button>
                          </div>
                        </div>
                        <p className="text-[11px] text-[#6B6A68] italic pt-1">Stored encrypted on your account. Never visible to any other user.</p>
                        <button
                          data-testid="yt-setup-save-keys"
                          onClick={saveKeys}
                          disabled={savingKeys}
                          className="text-[12px] px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {savingKeys ? "Saving…" : keysSaved ? "Re-save credentials" : "Save & continue"}
                        </button>
                      </div>
                    )}
                    {s.connectBtn && (
                      connectedChannel ? (
                        <div className="mt-1 flex items-center gap-2 px-4 py-2 rounded-md bg-[#EAF3EC] border border-[#B9D4BE] text-[#2F6B3A] text-[13px] font-medium" data-testid="yt-setup-connected-status">
                          <YoutubeLogo size={16} weight="fill" /> ✓ Connected as {connectedChannel.name}
                        </div>
                      ) : (
                        <div className="space-y-2 mt-1">
                          <button data-testid="yt-setup-connect"
                            onClick={connectChannel}
                            disabled={connecting || checkingIntegration}
                            className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#B54848] hover:bg-[#8f3838] disabled:bg-[#B54848]/40 text-white text-[13px] font-medium disabled:cursor-not-allowed"
                          >
                            <YoutubeLogo size={16} weight="fill" />
                            {checkingIntegration
                              ? "Checking credentials…"
                              : connecting
                                ? "Waiting for consent…"
                                : "Connect YouTube Channel"}
                          </button>
                          {!checkingIntegration && !keysSaved && (
                            <p className="text-[11.5px] text-[#B54848] italic" data-testid="yt-setup-connect-locked-note">
                              ↑ Save your Google Client ID + Secret in Step 3 first — this button is inert until that&apos;s done.
                            </p>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* § 4  Special keys reference */}
          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">④ Special keys & where they came from</div>
            <div className="border hairline rounded-lg overflow-hidden bg-white text-[12px]">
              {[
                { what: "Google Client ID", where: "Google Cloud Console → Credentials", stored: "This card's Settings" },
                { what: "Google Client Secret", where: "Google Cloud Console → Credentials", stored: "This card's Settings (encrypted)" },
                { what: "YouTube OAuth token", where: "Auto-issued when you clicked Connect", stored: "Auto-refreshed by My Silo" },
                { what: "Your BYOK LLM key", where: "Already in your Silo settings", stored: "Silo Settings → Keys" },
              ].map((r, i) => (
                <div key={i} className={`grid grid-cols-[1.2fr_1.4fr_1.2fr] gap-2 px-3 py-2 ${i > 0 ? "border-t hairline" : ""}`}>
                  <div className="font-medium text-[#1C1B1A]">{r.what}</div>
                  <div className="text-[#6B6A68]">{r.where}</div>
                  <div className="text-[#6B6A68]">{r.stored}</div>
                </div>
              ))}
            </div>
          </section>

          {/* § 5  Cost breakdown */}
          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">⑤ Cost breakdown</div>
            <ul className="space-y-1 text-[#3A3A38]">
              <li>• <strong>This add-on:</strong>{" "}
                <span data-testid="yt-replies-setup-price">
                  {skuCents == null ? "the one-time add-on price shown on the card" : `${fmt$(skuCents)} one-time`}
                </span>{" "}
                (unlocks Channel 1 + Channel 2 forever)</li>
              <li>• <strong>YouTube API:</strong> Free within Google&apos;s default daily quota (~1,000 replies/day)</li>
              <li>• <strong>LLM cost per reply:</strong> Uses your own BYOK key — ~$0.002 on Gemini Flash, ~$0.02 on Claude Sonnet</li>
            </ul>
            <div className="bg-[#EAF3EC]/60 border border-[#B9D4BE] rounded-md p-2.5 text-[12px] text-[#2F6B3A]">Total real-world cost for a typical creator: under $3/month in LLM spend.</div>
          </section>

              </div>
            )}
          </section>

          {/* § 6  Troubleshooting accordion */}
          <section className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">⑥ Troubleshooting</div>
            {TROUBLESHOOTING.map((t, i) => {
              const open2 = openTS === i;
              return (
                <div key={i} className={`rounded-md border hairline overflow-hidden ${open2 ? "bg-white" : "bg-white/60"}`}>
                  <button onClick={() => setOpenTS(open2 ? null : i)} data-testid={`yt-setup-ts-${i}`}
                    className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 text-[12.5px] text-[#1C1B1A]">
                    <span>{t.q}</span>
                    <CaretDown size={13} weight="bold" className={`shrink-0 text-[#4A5D4E] transition-transform ${open2 ? "rotate-180" : ""}`} />
                  </button>
                  {open2 && <div className="px-3 pb-2.5 pt-0 text-[12px] text-[#4A4A48] leading-relaxed">{t.a}</div>}
                </div>
              );
            })}
          </section>
        </div>

        {/* Sticky footer */}
        <div className="px-5 sm:px-6 py-3 border-t hairline shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] flex items-center justify-between gap-3">
          <span className="text-[11px] text-[#6B6A68] flex items-center gap-1.5">
            <LockKey size={12} weight="duotone" /> Locked — unlock to enable wiring
          </span>
          <button onClick={onClose} data-testid={`yt-replies-setup-done-${channelSlot}`}
            className="text-[12.5px] px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
            Got it <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// CrossSiloDetectBanner — Iter160e (Randy).
//
// Sits at the very top of the setup modal (below the header, above the
// § 1 walk-through). Fetches ALL of this user's YouTube OAuth rows via
// GET /youtube/integrations/mine, filters out the current (silo,slot)
// target, and if any OTHER rows exist offers a one-tap "mirror from
// {Channel Name} on {Silo}" button. Missing this banner = users have to
// paste the same Client ID + Secret across every silo × channel_slot
// pair they use.
// ─────────────────────────────────────────────────────────
function CrossSiloDetectBanner({ currentSiloId, currentChannelSlot, onMirror }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [busyRow, setBusyRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    ytApi.listMyIntegrations().then((data) => {
      if (cancelled) return;
      setRows(data || []);
    }).catch(() => setRows([])).finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Candidates = every row NOT matching this modal's target that
  // actually has usable Client ID credentials on file.
  const candidates = (rows || []).filter(
    (r) => r.has_client_creds && !(r.silo_id === currentSiloId && r.channel_slot === currentChannelSlot)
  );

  if (loading || dismissed || candidates.length === 0) return null;

  const modLabel = (slot) => (slot === "channel1" ? "Workbench YT Replies / YTRE" : "Refinery YT Replies");

  return (
    <div
      className="mx-5 sm:mx-6 mt-4 mb-2 border border-[#B9D4BE] bg-[#EAF3EC] rounded-md p-3.5"
      data-testid={`yt-setup-crosslink-banner-${currentChannelSlot}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#2F6B3A] font-semibold">Save yourself a step</div>
          <div className="text-[13px] text-[#1C1B1A] mt-1 leading-snug">
            You already connected YouTube on another silo or module. Mirror those credentials here in one tap — <span className="italic">you&apos;ll still complete a fresh Google sign-in to bind the channel for this surface.</span>
          </div>
          <div className="mt-2.5 flex flex-col gap-1.5">
            {candidates.slice(0, 4).map((r) => {
              const label = `${r.youtube_channel_name || "Channel"} on ${r.silo_name || r.silo_id?.slice(0, 8)} · ${modLabel(r.channel_slot)}`;
              return (
                <button
                  key={r.id}
                  disabled={!!busyRow}
                  data-testid={`yt-setup-crosslink-mirror-${r.id}`}
                  onClick={async () => {
                    setBusyRow(r.id);
                    try { await onMirror(r); } finally { setBusyRow(null); setDismissed(true); }
                  }}
                  className="text-left text-[12px] px-3 py-1.5 rounded-md bg-white border border-[#B9D4BE] hover:bg-[#DCEBDF] disabled:opacity-50 flex items-center gap-2"
                >
                  <ArrowRight size={11} weight="bold" className="text-[#2F6B3A] shrink-0" />
                  <span className="truncate">
                    {busyRow === r.id ? "Mirroring…" : "Mirror from"} <strong className="font-semibold">{label}</strong>
                  </span>
                </button>
              );
            })}
            {candidates.length > 4 && (
              <div className="text-[10.5px] text-[#6B6A68] italic pl-1">+ {candidates.length - 4} more on your account</div>
            )}
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded hover:bg-white shrink-0"
          data-testid={`yt-setup-crosslink-dismiss-${currentChannelSlot}`}
          title="Set up fresh instead"
        >
          <X size={14} className="text-[#6B6A68]" />
        </button>
      </div>
    </div>
  );
}

